import * as React from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { useEnterpriseAdminContext } from 'bundles/admin-dashboard/components/EnterpriseAdminContext';
import { useThirdPartyOrganization } from 'bundles/admin-dashboard/components/ThirdPartyOrganizationProvider';
import {
  getActivityReport,
  getMonthlyDigestReport,
  getProgramUsageReport,
} from 'bundles/enterprise-admin-analytics/constants/reports';
import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';

import deleteScheduleMutation from './mutations/DeleteSchedule.graphql';
import exportReportMutation from './mutations/ExportReport.graphql';
import saveScheduleMutation from './mutations/SaveSchedule.graphql';
import updateEnterpriseSettingMutation from './mutations/UpdateEnterpriseSetting.graphql';
import type { DeleteScheduleMutation, DeleteScheduleMutationVariables } from './mutations/__generated__/DeleteSchedule';
import type { ExportReportMutation, ExportReportMutationVariables } from './mutations/__generated__/ExportReport';
import type { SaveScheduleMutation, SaveScheduleMutationVariables } from './mutations/__generated__/SaveSchedule';
import type {
  UpdateEnterpriseSettingMutation,
  UpdateEnterpriseSettingMutationVariables,
} from './mutations/__generated__/UpdateEnterpriseSetting';
import analyticsReportsQuery from './queries/AnalyticsReports.graphql';
import analyticsSettingsQuery from './queries/AnalyticsSettings.graphql';
import type { AnalyticsReportsQuery, AnalyticsReportsQueryVariables } from './queries/__generated__/AnalyticsReports';
import type {
  AnalyticsSettingsQuery,
  AnalyticsSettingsQueryVariables,
} from './queries/__generated__/AnalyticsSettings';
import type { Report } from './types';

export const useAnalyticsSettingsMutation = () => {
  const [update] = useMutation<UpdateEnterpriseSettingMutation, UpdateEnterpriseSettingMutationVariables>(
    updateEnterpriseSettingMutation,
    { context: { clientName: 'gatewayGql' } }
  );
  return (
    orgId: string,
    key:
      | 'ENTERPRISE_SETTING_KEY_INSIGHTS_INCLUDE_BILLABLE_CONTRACTS'
      | 'ENTERPRISE_SETTING_KEY_INSIGHTS_INCLUDE_NON_BILLABLE_CONTRACTS',
    value: boolean
  ) => {
    return update({
      variables: {
        input: {
          settingId: {
            enterpriseSettingKey: key,
            managedLevelScope: {
              scope: 'THIRD_PARTY_ORGANIZATION',
              value: orgId,
            },
          },
          organizationId: orgId,
          enterpriseSettingType: {
            booleanEnterpriseSetting: value,
          },
        },
      },
    });
  };
};

// These inputs are mutually exclusive. Only 1 should be inputted at a time depending on the report type
export const useExportReportMutation = () => {
  const [exportReport] = useMutation<ExportReportMutation, ExportReportMutationVariables>(exportReportMutation, {
    context: { clientName: 'gatewayGql' },
  });

  return ({
    enrollment,
    membership,
  }: {
    enrollment?: ExportReportMutationVariables['input']['enrollment'];
    membership?: ExportReportMutationVariables['input']['membership'];
  }) => {
    return exportReport({
      variables: {
        input: {
          ...(enrollment ? { enrollment } : {}),
          ...(membership ? { membership } : {}),
        },
      },
    });
  };
};

export const useSaveScheduleMutation = () => {
  const [saveSchedule] = useMutation<SaveScheduleMutation, SaveScheduleMutationVariables>(saveScheduleMutation, {
    context: { clientName: 'gatewayGql' },
  });

  return ({
    enrollment,
    membership,
  }: {
    enrollment?: SaveScheduleMutationVariables['input']['enrollment'];
    membership?: SaveScheduleMutationVariables['input']['membership'];
  }) => {
    return saveSchedule({
      variables: {
        input: {
          ...(enrollment ? { enrollment } : {}),
          ...(membership ? { membership } : {}),
        },
      },
    });
  };
};

export const useDeleteScheduleMutation = () => {
  const [deleteSchedule] = useMutation<DeleteScheduleMutation, DeleteScheduleMutationVariables>(
    deleteScheduleMutation,
    {
      context: { clientName: 'gatewayGql' },
    }
  );
  return ({
    thirdPartyOrganizationId,
    reportType,
  }: {
    thirdPartyOrganizationId: string;
    reportType: DeleteScheduleMutationVariables['input']['reportType'];
  }) => {
    return deleteSchedule({
      variables: {
        input: {
          thirdPartyOrganizationId,
          reportType,
        },
      },
    });
  };
};

export const useEnableAnalyticsSettings = () => {
  const { hasNonBillableContracts, hasBillableContracts } = useEnterpriseAdminContext();
  const { getIsOrganizationDomainAdmin } = useEnterpriseAdminPermissions();
  const enableSettings = hasBillableContracts && hasNonBillableContracts && getIsOrganizationDomainAdmin();
  return enableSettings;
};

export const useAnalyticsSettings = () => {
  const { id: organizationId } = useThirdPartyOrganization();
  const { data, loading } = useQuery<AnalyticsSettingsQuery, AnalyticsSettingsQueryVariables>(analyticsSettingsQuery, {
    variables: { organizationId },
    context: { clientName: 'gatewayGql' },
  });
  const settings = data?.EnterpriseSetting?.findByOrganization.edges?.map((edge) => edge.node);
  const billableSettingEnabled = settings?.some(
    (setting) =>
      setting.data.includes('ENTERPRISE_SETTING_KEY_INSIGHTS_INCLUDE_BILLABLE_CONTRACTS') &&
      setting.data.includes('true')
  );
  const nonBillableSettingEnabled = settings?.some(
    (setting) =>
      setting.data.includes('ENTERPRISE_SETTING_KEY_INSIGHTS_INCLUDE_NON_BILLABLE_CONTRACTS') &&
      setting.data.includes('true')
  );
  return { billableSettingEnabled, nonBillableSettingEnabled, loading };
};

export const useAnalyticsReports = () => {
  const { id: thirdPartyOrganizationId } = useThirdPartyOrganization();
  const { data, loading, refetch } = useQuery<AnalyticsReportsQuery, AnalyticsReportsQueryVariables>(
    analyticsReportsQuery,
    {
      variables: { thirdPartyOrganizationId },
      context: { clientName: 'gatewayGql' },
    }
  );

  const reports = React.useMemo(() => {
    const reportsList: Report[] = [];

    const enrollmentReport = data?.EnterpriseReports?.findByOrganization?.elements.find(
      (report) => report.reportType === 'ENROLLMENT'
    );
    const membershipReport = data?.EnterpriseReports?.findByOrganization?.elements.find(
      (report) => report.reportType === 'MEMBERSHIP'
    );

    if (enrollmentReport) {
      const reportTemplate = getActivityReport();
      reportTemplate.thirdPartyOrganizationId = enrollmentReport.thirdPartyOrganizationId;
      reportTemplate.programIds = enrollmentReport.programs.map((program) => program.id);
      reportTemplate.includeEnrolledWithoutActivity = enrollmentReport.includeEnrolledWithoutActivity;
      reportTemplate.includeRemovedMembers = enrollmentReport.includeRemovedMembers;
      if (enrollmentReport.type) {
        reportTemplate.dateRangeType = enrollmentReport.type;
      }
      if (enrollmentReport.startDate) {
        reportTemplate.startDate = enrollmentReport.startDate;
      }
      if (enrollmentReport.endDate) {
        reportTemplate.endDate = enrollmentReport.endDate;
      }
      reportTemplate.schedule = enrollmentReport.schedule;
      reportTemplate.scheduled = Object.values(enrollmentReport.schedule).some((value) => value === true);
      reportsList.push(reportTemplate);
    } else {
      reportsList.push(getActivityReport());
    }

    if (membershipReport) {
      const reportTemplate = getProgramUsageReport();
      reportTemplate.thirdPartyOrganizationId = membershipReport.thirdPartyOrganizationId;
      reportTemplate.programIds = membershipReport.programs.map((program) => program.id);
      reportTemplate.includeInvitedLearners = membershipReport.includeInvitedLearners;
      reportTemplate.includeJoinedLearners = membershipReport.includeJoinedLearners;
      reportTemplate.includeRemovedMembers = membershipReport.includeRemovedMembers;
      reportTemplate.schedule = membershipReport.schedule;
      reportTemplate.scheduled = Object.values(membershipReport.schedule).some((value) => value === true);
      reportsList.push(reportTemplate);
    } else {
      reportsList.push(getProgramUsageReport());
    }

    reportsList.push(getMonthlyDigestReport());

    return reportsList;
  }, [data]);

  return { reports, reportsLoading: loading, refetch };
};
